// Step 1 — Account aanmaken (T11). Vervangt de eerste helft van het oude
// Register.jsx → CreateAccount.jsx pad.
//
// Verantwoordelijk voor:
//   - Firebase auth-user (createUserWithEmailAndPassword)
//   - Users-doc (via bestaande `createProfile` Cloud Function)
//   - CompagnyMeta + bijbehorende defaults (via `createProjectAccount` Cloud
//     Function — placeholders voor `compagny`/`logo`/`website`, ingevuld in Step 2)
//   - Resolutie van deeplink (partner + network) → modules + lid-relaties
//   - `onboardingStatus.lastStep: 2` op de nieuwe CompagnyMeta zodat resume-on-return werkt
//   - Foundational identifiers (compagnyId, userId) opslaan in OnboardingFlowContext

import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
// Full bundle, like every other httpsCallable caller: `firebase/app` alone does
// not register the .functions() accessor.
import firebase from '../../../firebase/compat'
import { auth } from '../../../firebase/config'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import parseDeeplink from '../../../helpers/onboardingHelpers/parseDeeplink'
import normalizeEmail from '../../../helpers/normalizeEmail'
import validateStep1Account from '../../../helpers/onboardingHelpers/validateStep1Account'
import { TERMS_URL, PRIVACY_URL, TERMS_VERSION, PRIVACY_VERSION } from '../../../helpers/legal/legalDocs'
import useDevPreview from '../../../hooks/useDevPreview'

const Step1Account = ({ onNext }) => {

    // Form state
    const [forname, setForname] = useState('')
    const [surname, setSurname] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordRepeat, setPasswordRepeat] = useState('')
    const [acceptedTerms, setAcceptedTerms] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    // Context — schrijft aan het einde van de submit. Partner komt uit context
    // (orchestrator subscribet één keer; zie OnboardingFlow.jsx).
    const {
        setCompagnyId,
        userId,
        setUserId,
        setFormState,
        setModules,
        partner,
    } = useContext(OnboardingFlowContext)

    // Deeplink — bepaalt initial modules + Parent-array
    const location = useLocation()
    const deeplink = useMemo(() => parseDeeplink(location.search), [location.search])

    // Dev-preview: bypass alle Firebase-writes (zie useDevPreview hook)
    const { active: devPreview } = useDevPreview()

    // Detecteer of er al een actieve Firebase-auth-sessie is. Zo ja, dan
    // moet de gebruiker eerst uitloggen — anders schrijven de Cloud Functions
    // straks de nieuwe org tegen de UID van de oude gebruiker
    // (verifyCallerOwnsCompagny → "Caller hoort niet bij deze tenant").
    const [currentUser, setCurrentUser] = useState(null)
    useEffect(() => auth.onAuthStateChanged(setCurrentUser), [])

    const handleSignOut = async () => {
        await auth.signOut()
        setError('')
    }

    const initialModules = useMemo(() => ({
        impactMeten: deeplink.baseEnabled || deeplink.moduleSlugs.includes('impactMeten'),
        personas: deeplink.moduleSlugs.includes('personas'),
        housevisits: deeplink.moduleSlugs.includes('housevisits'),
        portfolio: deeplink.moduleSlugs.includes('portfolio'),
    }), [deeplink])

    const networkCompagnyIDs = useMemo(() => {
        const ids = []
        if (partner && partner.networkCompagnyID) ids.push(partner.networkCompagnyID)
        if (deeplink.networkCompagnyID) ids.push(deeplink.networkCompagnyID)
        return [...new Set(ids)]
    }, [partner, deeplink.networkCompagnyID])

    const submit = async (e) => {
        e.preventDefault()
        setError('')

        // Dev-preview: skip ALLE validatie + Firebase-writes. Gebruiker kan
        // zonder invullen door de design-preview klikken. Fake-id's gaan in
        // context zodat Step 2+ niet faalt op een ontbrekende sessie.
        if (devPreview) {
            const fakeCompagnyId = `dev-${uuid().slice(0, 8)}`
            const fakeUserId = `dev-uid-${uuid().slice(0, 8)}`
            setCompagnyId(fakeCompagnyId)
            setUserId(fakeUserId)
            setModules(initialModules)
            setFormState((prev) => ({
                ...prev,
                account: { forname, surname, email: normalizeEmail(email) },
            }))
            onNext()
            return
        }

        // Guard: weiger als er al een Firebase-auth-sessie loopt die NIET door
        // deze onboarding-flow is aangemaakt. createUserWithEmailAndPassword
        // zou anders óf de oude sessie overschrijven, óf (bij email-already-
        // in-use) silently de oude sessie behouden — waardoor downstream
        // verifyCallerOwnsCompagny faalt. uid-vergelijking met context.userId
        // sluit de "we hebben hem net gecreëerd"-race uit.
        if (currentUser && currentUser.uid !== userId) {
            setError(`Je bent al ingelogd als ${currentUser.email}. Log eerst uit voordat je een nieuw account aanmaakt.`)
            return
        }

        const validationError = validateStep1Account({ forname, email, password, passwordRepeat, acceptedTerms })
        if (validationError) {
            setError(validationError)
            return
        }

        setLoading(true)

        try {
            const compagnyId = uuid()
            const profileId = uuid()

            // 1. Firebase auth user
            // Eén genormaliseerd adres voor het hele pad — een geplakt adres
            // sleept een onzichtbare spatie mee en splitst zich anders in twee
            // verschillende mensen. Zie docs/inbox.md Bug #137.
            // AI-CONTEXT: See docs/pitfalls.md#database-untrimmed-email-breaks-exact-match
            const accountEmail = normalizeEmail(email)
            const cred = await auth.createUserWithEmailAndPassword(accountEmail, password)
            const userId = cred.user.uid

            // Persist userId NOW (niet pas na de Cloud Functions) — anders is
            // currentUser tijdens de hele cold-start van createProfile +
            // createProjectAccount ingelogd terwijl userId in context nog null
            // staat. De "Je bent al ingelogd"-banner gaat dan flashen omdat
            // currentUser.uid !== userId.
            setUserId(userId)

            // 2. Users-doc via bestaande createProfile-functie
            // AI-CONTEXT: See docs/pitfalls.md#api-firebase-v1-region-default
            // createProfile is still v1 in us-central1 — the default client is
            // correct. The move to europe-west1 needs the function and a
            // production hosting release to ship together; see index.js.
            const createProfile = firebase.functions().httpsCallable('createProfile')
            const profileResult = await createProfile({ data: {
                email: accountEmail,
                forname,
                surname,
                photo: '',
                uid: userId,
                client: compagnyId,
                id: profileId,
                approved: true,   // Stripe-betaling is de gate — geen email-verificatie nodig
                // Consent — de Cloud Function stempelt acceptedAt + ipAddress
                // server-side en schrijft de LegalConsents-auditregel. Wij sturen
                // alleen de versies mee die de gebruiker zag.
                acceptedTerms: true,
                termsVersion: TERMS_VERSION,
                privacyVersion: PRIVACY_VERSION,
            }})
            if (profileResult.data !== 'success') {
                throw new Error('Profile-aanmaak mislukt')
            }

            // 3. CompagnyMeta + Subscriptions + bijhorigheid via createProjectAccount
            const createProjectAccount = firebase.functions().httpsCallable('createProjectAccount')
            const result = await createProjectAccount({ data: {
                CompagnyId: compagnyId,
                compagny: '',                  // ingevuld in Step 2
                logo: '',                      // mogelijk ingevuld door branding-fetch (T27)
                ImpactManager: 'Deccos',
                Parent: null,                  // legacy single-parent slot — ongebruikt hier
                Subscription: 'trial',
                Website: '',                   // ingevuld in Step 2
                onboarding: true,
                subscriptionType: 'free',
                email: accountEmail,
                modules: initialModules,
                partner: deeplink.partnerSlug || null,
                centralEmail: '',              // ingevuld in Step 2
                onboardingStatus: { lastStep: 2, completed: false },
                networkCompagnyIDs,
            }})
            if (result.data !== 'success') {
                throw new Error('Account-aanmaak mislukt')
            }

            // 4. Persisteer foundationals + form-data in context
            setCompagnyId(compagnyId)
            setUserId(userId)
            setModules(initialModules)
            setFormState((prev) => ({
                ...prev,
                account: { forname, surname, email: accountEmail },
            }))

            // 5. Door naar Step 2
            onNext()

        } catch (err) {
            // LET OP: hier wordt niets opgeruimd. Faalt createProjectAccount,
            // dan blijven het inlogaccount én het profiel staan, en wijst dat
            // profiel naar een omgeving die niet bestaat. Zie docs/inbox.md Bug #139.
            // AI-CONTEXT: See docs/pitfalls.md#state-multi-step-signup-leaves-half-an-account
            console.error('Step1Account submit error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setLoading(false)
        }
    }

    return (
        <div className="onboarding-step">
            <h1>Maak je account aan</h1>
            <p className="onboarding-step-intro">
                Eerst even de basis. In de volgende stap stellen we je organisatie in.
            </p>

            <form className="onboarding-form" onSubmit={submit}>
                <div className="onboarding-field">
                    <label>Voornaam *</label>
                    <input
                        type="text"
                        value={forname}
                        onChange={(e) => setForname(e.target.value)}
                        placeholder="Voornaam"
                        autoComplete="given-name"
                    />
                </div>

                <div className="onboarding-field">
                    <label>Achternaam</label>
                    <input
                        type="text"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        placeholder="Achternaam"
                        autoComplete="family-name"
                    />
                </div>

                <div className="onboarding-field">
                    <label>E-mailadres *</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="naam@organisatie.nl"
                        autoComplete="email"
                    />
                </div>

                <div className="onboarding-field">
                    <label>Wachtwoord *</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Wachtwoord"
                        autoComplete="new-password"
                    />
                </div>

                <div className="onboarding-field">
                    <label>Herhaal wachtwoord *</label>
                    <input
                        type="password"
                        value={passwordRepeat}
                        onChange={(e) => setPasswordRepeat(e.target.value)}
                        placeholder="Herhaal wachtwoord"
                        autoComplete="new-password"
                    />
                </div>

                <div className="onboarding-consent-row">
                    <input
                        type="checkbox"
                        id="onboarding-accept-terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <label htmlFor="onboarding-accept-terms">
                        Ik ga akkoord met de{' '}
                        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">algemene voorwaarden</a>
                        {' '}en het{' '}
                        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">privacybeleid</a>.
                    </label>
                </div>

                {!devPreview && !loading && currentUser && currentUser.uid !== userId && (
                    <div className="onboarding-error">
                        <p>
                            Je bent al ingelogd als <strong>{currentUser.email}</strong>.
                            Log eerst uit voordat je een nieuw account aanmaakt.
                        </p>
                        <button
                            type="button"
                            className="onboarding-button-secondary"
                            onClick={handleSignOut}
                        >
                            Log uit
                        </button>
                    </div>
                )}

                {error && <p className="onboarding-error">{error}</p>}

                <div className="onboarding-step-actions">
                    <button type="submit" disabled={loading || (!devPreview && !!currentUser && currentUser.uid !== userId)}>
                        {loading ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Volgende'}
                    </button>
                </div>
            </form>
        </div>
    )

}

export default Step1Account
